import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { inspectionsAPI } from "../../../services/api";
import { isSystemAdmin } from "../../../constants/systemRoles";
import { ListChecks, Plus, Trash2, Save, Edit3, Wand2 } from "lucide-react";
import RequirementIntelligenceView from "./RequirementIntelligenceView";

function extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
  }

  // Simple string detail
  if (detail && typeof detail === "string") {
    return detail;
  }

  // Object detail (non-array)
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return "Validation error";
    }
  }

  if (err?.message) return err.message;

  return "Unexpected error.";
}

const SUBTABS = [
  { id: "templates", label: "Templates" },
  { id: "requirement-intelligence", label: "Requirement Intelligence" },
  ...(process.env.REACT_APP_LEGACY_AI_DESIGN_ENABLED === "true"
    ? [{ id: "ai-design", label: "Legacy AI Design" }]
    : []),
];

const InspectionTemplatesView = () => {
  const { activeProject, projectRole, currentUser } = useProjectContext();
  const projectId = activeProject?.id;

  const isAdmin = isSystemAdmin(currentUser?.role);
  // const canManageTemplates =
  //   isAdmin || projectRole === "qa_qc_manager" || projectRole === "inspector";
  const canManageTemplates =
    isAdmin || ["qa_qc_manager", "inspector"].includes(projectRole);

  // -----------------------------
  // Common state
  // -----------------------------
  const [activeSubtab, setActiveSubtab] = useState("templates");
  const [error, setError] = useState(null);

  // -----------------------------
  // 1) Manual template management (Templates tab)
  // -----------------------------
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [inspectionTypesLoading, setInspectionTypesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeDescription, setNewTypeDescription] = useState("");
  const [savingType, setSavingType] = useState(false);
  const [savingTypeChanges, setSavingTypeChanges] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);

  const [editedTypeName, setEditedTypeName] = useState("");
  const [editedTypeDescription, setEditedTypeDescription] = useState("");

  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [itemSavingId, setItemSavingId] = useState(null);
  const [itemDeletingId, setItemDeletingId] = useState(null);

  // NEW: include inspection_note + extra fields from the start
  const [newItem, setNewItem] = useState({
    item_text: "",
    item_category: "",
    inspection_note: "",
    order_index: "",
    is_required: true,
    section_code: "",
    section_title: "",
    part_label: "",
    canonical_role: "",
  });

  // ---- Load types ----
  const loadInspectionTypes = useCallback(async () => {
    if (!projectId) return;
    setInspectionTypesLoading(true);
    setError(null);

    try {
      const res = await inspectionsAPI.listProjectInspectionTypes(projectId);
      const rows = res.data || [];
      setInspectionTypes(rows);

      // Keep selected type if still present, otherwise pick first
      if (rows.length > 0) {
        const exists = rows.find((t) => t.id === selectedTypeId);
        const idToUse = exists ? exists.id : rows[0].id;
        setSelectedTypeId(idToUse);
        setEditedTypeName(exists ? exists.name || "" : rows[0].name || "");
        setEditedTypeDescription(
          exists ? exists.description || "" : rows[0].description || "",
        );
      } else {
        setSelectedTypeId(null);
        setEditedTypeName("");
        setEditedTypeDescription("");
        setChecklistItems([]);
      }
    } catch (err) {
      console.error("Failed to load project inspection types", err);
      setInspectionTypes([]);
      setError(extractErrorMessage(err));
    } finally {
      setInspectionTypesLoading(false);
    }
  }, [projectId, selectedTypeId]);

  useEffect(() => {
    loadInspectionTypes();
  }, [loadInspectionTypes]);

  // ---- Load checklist items when type changes ----
  const loadChecklistItems = useCallback(async () => {
    if (!projectId || !selectedTypeId) {
      setChecklistItems([]);
      return;
    }
    setChecklistLoading(true);
    setError(null);

    try {
      const res = await inspectionsAPI.listProjectInspectionChecklistItems(
        projectId,
        selectedTypeId,
      );
      setChecklistItems(res.data || []);
    } catch (err) {
      console.error("Failed to load checklist template items", err);
      setChecklistItems([]);
      setError(extractErrorMessage(err));
    } finally {
      setChecklistLoading(false);
    }
  }, [projectId, selectedTypeId]);

  useEffect(() => {
    if (selectedTypeId) {
      loadChecklistItems();
      const found = inspectionTypes.find((t) => t.id === selectedTypeId);
      setEditedTypeName(found?.name || "");
      setEditedTypeDescription(found?.description || "");
    }
  }, [selectedTypeId, loadChecklistItems, inspectionTypes]);

  const selectedType = useMemo(
    () => inspectionTypes.find((t) => t.id === selectedTypeId) || null,
    [inspectionTypes, selectedTypeId],
  );

  // ---- Create new inspection type ----
  const handleCreateType = async (e) => {
    e.preventDefault();
    if (!projectId || !newTypeName.trim()) return;
    setSavingType(true);
    setError(null);

    try {
      const payload = {
        name: newTypeName.trim(),
        description: newTypeDescription.trim() || null,
        status: "approved",
      };
      const res = await inspectionsAPI.createProjectInspectionType(
        projectId,
        payload,
      );
      const created = res.data;

      const updatedList = [...inspectionTypes, created].sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      );
      setInspectionTypes(updatedList);
      setSelectedTypeId(created.id);
      setEditedTypeName(created.name || "");
      setEditedTypeDescription(created.description || "");

      setNewTypeName("");
      setNewTypeDescription("");
    } catch (err) {
      console.error("Failed to create inspection type", err);
      setError(extractErrorMessage(err));
    } finally {
      setSavingType(false);
    }
  };

  // ---- Save edits to selected type ----
  const handleSaveTypeChanges = async () => {
    if (!projectId || !selectedTypeId) return;
    setSavingTypeChanges(true);
    setError(null);

    try {
      const payload = {
        name: editedTypeName.trim() || selectedType.name,
        description:
          editedTypeDescription.trim() || selectedType.description || null,
      };

      const res = await inspectionsAPI.updateProjectInspectionType(
        projectId,
        selectedTypeId,
        payload,
      );
      const updated = res.data;

      setInspectionTypes((prev) =>
        prev.map((t) => (t.id === updated.id ? updated : t)),
      );
    } catch (err) {
      console.error("Failed to update inspection type", err);
      setError(extractErrorMessage(err));
    } finally {
      setSavingTypeChanges(false);
    }
  };

  // ---- Delete inspection type ----
  const handleDeleteType = async (typeId) => {
    if (!projectId || !typeId) return;
    if (
      !window.confirm(
        "Delete this inspection type and all its checklist template items?",
      )
    ) {
      return;
    }

    setDeletingTypeId(typeId);
    setError(null);

    try {
      await inspectionsAPI.deleteProjectInspectionType(projectId, typeId);
      const remaining = inspectionTypes.filter((t) => t.id !== typeId);
      setInspectionTypes(remaining);

      if (selectedTypeId === typeId) {
        if (remaining.length > 0) {
          setSelectedTypeId(remaining[0].id);
        } else {
          setSelectedTypeId(null);
          setChecklistItems([]);
          setEditedTypeName("");
          setEditedTypeDescription("");
        }
      }
    } catch (err) {
      console.error("Failed to delete inspection type", err);
      setError(extractErrorMessage(err));
    } finally {
      setDeletingTypeId(null);
    }
  };

  // ---- Inline editing helpers for checklist items ----
  const updateItemLocal = (itemId, patch) => {
    setChecklistItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
    );
  };

  // ---- Save / update a single checklist item ----
  const handleSaveItem = async (item) => {
    if (!projectId || !selectedTypeId || !item?.id) return;
    setItemSavingId(item.id);
    setError(null);

    try {
      const payload = {
        item_text: item.item_text?.trim() || null,
        item_category: item.item_category?.trim() || null,
        inspection_note: item.inspection_note?.trim() || null,
        order_index:
          item.order_index === "" || item.order_index === null
            ? null
            : Number(item.order_index),
        is_required:
          typeof item.is_required === "boolean" ? item.is_required : true,
        section_code: item.section_code || null,
        section_title: item.section_title || null,
        part_label: item.part_label || null,
        canonical_role: item.canonical_role || null,
      };

      const res = await inspectionsAPI.updateProjectInspectionChecklistItem(
        projectId,
        selectedTypeId,
        item.id,
        payload,
      );
      const updated = res.data;

      setChecklistItems((prev) =>
        prev.map((it) => (it.id === updated.id ? updated : it)),
      );
    } catch (err) {
      console.error("Failed to update checklist item", err);
      setError(extractErrorMessage(err));
    } finally {
      setItemSavingId(null);
    }
  };

  // ---- Delete checklist item ----
  const handleDeleteItem = async (itemId) => {
    if (!projectId || !selectedTypeId || !itemId) return;
    if (!window.confirm("Delete this checklist template item?")) return;

    setItemDeletingId(itemId);
    setError(null);

    try {
      await inspectionsAPI.deleteProjectInspectionChecklistItem(
        projectId,
        selectedTypeId,
        itemId,
      );
      setChecklistItems((prev) => prev.filter((it) => it.id !== itemId));
    } catch (err) {
      console.error("Failed to delete checklist item", err);
      setError(extractErrorMessage(err));
    } finally {
      setItemDeletingId(null);
    }
  };

  // ---- Create new checklist item ----
  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!projectId || !selectedTypeId || !newItem.item_text.trim()) return;
    setItemSavingId("new");
    setError(null);

    try {
      const payload = {
        inspection_type_id: selectedTypeId,
        item_text: newItem.item_text.trim(),
        item_category: newItem.item_category.trim() || null,
        inspection_note: newItem.inspection_note.trim() || null, // NEW
        order_index:
          newItem.order_index === "" || newItem.order_index === null
            ? null
            : Number(newItem.order_index),
        is_required: !!newItem.is_required,
        section_code: newItem.section_code?.trim() || null,
        section_title: newItem.section_title?.trim() || null,
        part_label: newItem.part_label?.trim() || null,
        canonical_role: newItem.canonical_role?.trim() || null,
      };

      const res = await inspectionsAPI.createProjectInspectionChecklistItem(
        projectId,
        selectedTypeId,
        payload,
      );
      const created = res.data;
      if (created) {
        setChecklistItems((prev) => [...prev, created]);
      }

      setNewItem({
        item_text: "",
        item_category: "",
        inspection_note: "", // NEW
        order_index: "",
        is_required: true,
        section_code: "",
        section_title: "",
        part_label: "",
        canonical_role: "",
      });
    } catch (err) {
      console.error("Failed to create checklist item", err);
      setError(extractErrorMessage(err));
    } finally {
      setItemSavingId(null);
    }
  };

  // -----------------------------
  // 2) AI Design tab – NEW implementation (matches your snippet)
  // -----------------------------

  // Raw LLM response
  const [preview, setPreview] = useState(null);

  // Draft editable LLM output
  const [draftTypes, setDraftTypes] = useState([]); // [{ id, enabled, spec, description }]
  const [draftChecklists, setDraftChecklists] = useState([]); // [{ id, enabled, item_text, inspection_note }]

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Optional focus scope (discipline / section / system)
  const [focusScope, setFocusScope] = useState("");

  // Free-form LLM design instructions
  const [designInstructions, setDesignInstructions] = useState("");

  const currentCount = useMemo(
    () => (inspectionTypes || []).length,
    [inspectionTypes],
  );

  const hasPreview =
    !!preview && (draftTypes.length > 0 || draftChecklists.length > 0);

  // ===== RAG: generate proposed templates from new LLM schema =====
  const handleGenerateFromRag = useCallback(async () => {
    if (!projectId) return;

    setLoadingPreview(true);
    setError(null);

    try {
      const payload = {
        // Optional scope – if empty, backend falls back to generic/default
        focus_scope: focusScope && focusScope.trim() ? focusScope.trim() : null,
        seed_question: null,
        // Free-form instructions – user fully controls what they want
        design_instructions:
          designInstructions && designInstructions.trim()
            ? designInstructions.trim()
            : null,
      };

      const res = await inspectionsAPI.ragInspectionDesign(projectId, payload);
      const data = res?.data || {};

      const rawTypes = Array.isArray(data.inspection_types)
        ? data.inspection_types
        : [];
      const rawChecklists = Array.isArray(data.checklists)
        ? data.checklists
        : [];

      if (!rawTypes.length && !rawChecklists.length) {
        setPreview(null);
        setDraftTypes([]);
        setDraftChecklists([]);
        setError(
          "RAG did not return any inspection types or checklist items. " +
            "Try a different scope (e.g. 'HVAC', 'Fire Protection', 'Switchboards', or a specific section).",
        );
        return;
      }

      // Map new schema → editable draft state
      const mappedTypes = rawTypes.map((t, idx) => {
        let specText = "";
        if (typeof t?.spec === "string") {
          specText = t.spec;
        } else if (t?.spec != null) {
          // If spec is an object, stringify lightly so user sees it
          try {
            specText = JSON.stringify(t.spec);
          } catch {
            specText = String(t.spec);
          }
        } else if (focusScope && focusScope.trim()) {
          specText = focusScope.trim();
        } else {
          specText = `Spec ${idx + 1}`;
        }

        return {
          id: t.id ?? `rag-type-${idx}`,
          enabled: true,
          spec: specText,
          description: t.description || "",
        };
      });

      const mappedChecklists = rawChecklists.map((c, idx) => ({
        id: c.id ?? `rag-item-${idx}`,
        enabled: true,
        item_text: c.item_text || "",
        inspection_note: c.inspection_note || "",
      }));

      setPreview(data);
      setDraftTypes(mappedTypes);
      setDraftChecklists(mappedChecklists);
    } catch (e) {
      console.error("ragInspectionDesign failed", e);
      const msg = extractErrorMessage(e);
      setError(msg);
      setPreview(null);
      setDraftTypes([]);
      setDraftChecklists([]);
    } finally {
      setLoadingPreview(false);
    }
  }, [projectId, focusScope, designInstructions]);

  // ===== Local editing helpers (types) =====
  const updateTypeField = useCallback((typeId, field, value) => {
    setDraftTypes((prev) =>
      prev.map((t) => (t.id === typeId ? { ...t, [field]: value } : t)),
    );
  }, []);

  const toggleTypeEnabled = useCallback((typeId) => {
    setDraftTypes((prev) =>
      prev.map((t) => (t.id === typeId ? { ...t, enabled: !t.enabled } : t)),
    );
  }, []);

  // ===== Local editing helpers (checklist items) =====
  const updateChecklistField = useCallback((itemId, field, value) => {
    setDraftChecklists((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, [field]: value } : it)),
    );
  }, []);

  const toggleChecklistEnabled = useCallback((itemId) => {
    setDraftChecklists((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, enabled: !it.enabled } : it,
      ),
    );
  }, []);

  // ===== Save approved templates to backend (new simplified payload) =====
  const handleSave = useCallback(async () => {
    if (!projectId) return;
    if (!hasPreview) return;

    setSaving(true);
    setError(null);

    try {
      const selectedTypes = draftTypes.filter(
        (t) => t.enabled && (t.spec?.trim() || t.description?.trim()),
      );
      const selectedItems = draftChecklists.filter(
        (it) =>
          it.enabled && (it.item_text?.trim() || it.inspection_note?.trim()),
      );

      if (!selectedTypes.length && !selectedItems.length) {
        setError(
          "Select at least one inspection type or checklist item to save.",
        );
        setSaving(false);
        return;
      }

      const inspectionTypesPayload = selectedTypes.map((t) => ({
        spec: t.spec?.trim() || null,
        description: t.description?.trim() || null,
      }));

      const checklistsPayload = selectedItems.map((it) => ({
        item_text: (it.item_text || "").trim(),
        inspection_note: it.inspection_note?.trim() || null,
      }));

      const payload = {
        inspection_types: inspectionTypesPayload,
        checklists: checklistsPayload,
        auto_approve: true,
      };

      await inspectionsAPI.saveRagInspectionDesign(projectId, payload);

      // Refresh project inspection types count
      setInspectionTypesLoading(true);
      try {
        const res = await inspectionsAPI.listProjectInspectionTypes(projectId);
        setInspectionTypes(res.data || []);
      } catch (e) {
        console.error("Reload project inspection types failed", e);
      } finally {
        setInspectionTypesLoading(false);
      }

      // Clear preview after saving
      setPreview(null);
      setDraftTypes([]);
      setDraftChecklists([]);
    } catch (e) {
      console.error("saveRagInspectionDesign failed", e);
      const msg = extractErrorMessage(e);
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [projectId, draftTypes, draftChecklists, hasPreview]);

  // -----------------------------
  // Render
  // -----------------------------
  if (!projectId) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Inspection Templates
        </h2>
        <p className="text-sm text-gray-500">
          Select a project from the left sidebar to manage its inspection
          templates.
        </p>
      </div>
    );
  }

  if (!canManageTemplates) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Inspection Templates
        </h2>
        <p className="text-sm text-gray-500">
          You do not have permission to manage inspection templates for this
          project. Please contact the Project Manager or QA/QC Manager.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <ListChecks className="w-5 h-5" />
            Inspection Templates
          </h1>
          <p className="text-xs text-gray-500">
            Project-specific inspection types and checklist templates used when
            creating inspections.
          </p>
        </div>

        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 overflow-hidden text-xs">
          {SUBTABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveSubtab(tab.id)}
              className={[
                "px-3 py-1.5 flex items-center gap-1",
                activeSubtab === tab.id
                  ? "bg-white text-gray-900"
                  : "text-gray-500 hover:text-gray-800",
              ].join(" ")}
            >
              {tab.id === "templates" ? (
                <ListChecks className="w-3 h-3" />
              ) : (
                <Wand2 className="w-3 h-3" />
              )}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
          {error}
        </div>
      )}

      {/* ========= TEMPLATES TAB ========= */}
      {activeSubtab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,0.3fr)_minmax(0,0.7fr)] gap-4">
          {/* LEFT: Types list + add form */}
          <div className="flex flex-col border border-gray-200 rounded-lg bg-white">
            <div className="border-b border-gray-200 px-3 py-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-gray-700">
                  Inspection Types
                </div>
                <div className="text-[11px] text-gray-400">
                  {inspectionTypesLoading
                    ? "Loading..."
                    : `${inspectionTypes.length} type(s)`}
                </div>
              </div>
            </div>

            <div className="p-3 space-y-3">
              <form
                onSubmit={handleCreateType}
                className="space-y-2 rounded-md border border-dashed border-gray-200 bg-gray-50 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-medium text-gray-700 flex items-center gap-1">
                    <Plus className="w-3 h-3" />
                    New inspection type
                  </div>
                  <button
                    type="submit"
                    disabled={savingType || !newTypeName.trim()}
                    className={[
                      "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-white",
                      savingType || !newTypeName.trim()
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-900 hover:bg-black",
                    ].join(" ")}
                  >
                    <Save className="w-3 h-3" />
                    {savingType ? "Saving…" : "Add"}
                  </button>
                </div>
                <input
                  type="text"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                  placeholder="Type name (e.g. LV Switchboard Installation)"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                />
                <textarea
                  rows={2}
                  className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                  placeholder="Short description (optional)"
                  value={newTypeDescription}
                  onChange={(e) => setNewTypeDescription(e.target.value)}
                />
              </form>

              <div className="border-t border-gray-100 pt-2">
                {inspectionTypes.length === 0 && !inspectionTypesLoading && (
                  <div className="text-[11px] text-gray-500">
                    No inspection types yet. Create one above or use Requirement
                    Intelligence to propose templates.
                  </div>
                )}

                <div className="space-y-1 max-h-[320px] overflow-auto">
                  {inspectionTypes.map((t) => {
                    const isSelected = t.id === selectedTypeId;
                    const isDeleting = deletingTypeId === t.id;

                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setSelectedTypeId(t.id)}
                        className={[
                          "w-full flex items-start justify-between gap-2 rounded px-2 py-1.5 text-left text-xs border",
                          isSelected
                            ? "border-gray-900 bg-gray-900 text-white"
                            : "border-gray-100 bg-gray-50 hover:bg-gray-100",
                        ].join(" ")}
                      >
                        <div>
                          <div className="font-medium truncate">
                            {t.name || "(no name)"}
                          </div>
                          {t.description && (
                            <div
                              className={
                                isSelected
                                  ? "text-[11px] text-gray-100 line-clamp-2"
                                  : "text-[11px] text-gray-500 line-clamp-2"
                              }
                            >
                              {t.description}
                            </div>
                          )}
                          {t.status && (
                            <div
                              className={
                                isSelected
                                  ? "mt-0.5 inline-flex rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-100"
                                  : "mt-0.5 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-[10px] text-gray-700"
                              }
                            >
                              {t.status}
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteType(t.id);
                          }}
                          className={[
                            "shrink-0 inline-flex items-center justify-center rounded-full p-1 text-[10px]",
                            isSelected
                              ? "text-gray-100 hover:bg-gray-800"
                              : "text-gray-400 hover:bg-gray-200",
                          ].join(" ")}
                          disabled={isDeleting}
                          title="Delete type"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Selected type details + checklist items */}
          <div className="flex flex-col border border-gray-200 rounded-lg bg-white min-h-[320px]">
            <div className="border-b border-gray-200 px-3 py-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <Edit3 className="w-3 h-3" />
                  {selectedType
                    ? `Edit "${selectedType.name}"`
                    : "Select a type"}
                </div>
                <div className="text-[11px] text-gray-400">
                  Edit type name/description and its checklist template.
                </div>
              </div>
            </div>

            {!selectedType && (
              <div className="p-4 text-[11px] text-gray-500">
                Select or create an inspection type on the left.
              </div>
            )}

            {selectedType && (
              <div className="flex-1 flex flex-col p-3 gap-3 overflow-hidden">
                {/* Type header form */}
                <div className="space-y-2 border border-gray-100 rounded-md p-2 bg-gray-50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-gray-700">
                      Type details
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveTypeChanges}
                      disabled={savingTypeChanges}
                      className={[
                        "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-white",
                        savingTypeChanges
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gray-900 hover:bg-black",
                      ].join(" ")}
                    >
                      <Save className="w-3 h-3" />
                      {savingTypeChanges ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                  <input
                    type="text"
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                    value={editedTypeName}
                    onChange={(e) => setEditedTypeName(e.target.value)}
                  />
                  <textarea
                    rows={2}
                    className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                    value={editedTypeDescription}
                    onChange={(e) => setEditedTypeDescription(e.target.value)}
                  />
                </div>

                {/* Checklist items */}
                <div className="flex-1 flex flex-col gap-2 overflow-hidden">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-gray-700">
                      Checklist template items
                    </div>
                    {checklistLoading && (
                      <div className="text-[10px] text-gray-400">Loading…</div>
                    )}
                  </div>

                  {/* Add new item */}
                  <form
                    onSubmit={handleCreateItem}
                    className="space-y-2 rounded-md border border-dashed border-gray-200 bg-gray-50 p-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-medium text-gray-700 flex items-center gap-1">
                        <Plus className="w-3 h-3" />
                        New checklist item
                      </div>
                      <button
                        type="submit"
                        disabled={
                          itemSavingId === "new" || !newItem.item_text.trim()
                        }
                        className={[
                          "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-white",
                          itemSavingId === "new" || !newItem.item_text.trim()
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gray-900 hover:bg-black",
                        ].join(" ")}
                      >
                        <Save className="w-3 h-3" />
                        {itemSavingId === "new" ? "Saving…" : "Add"}
                      </button>
                    </div>

                    {/* Text + Note */}
                    <textarea
                      rows={2}
                      className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                      placeholder="Checklist item text…"
                      value={newItem.item_text}
                      onChange={(e) =>
                        setNewItem((prev) => ({
                          ...prev,
                          item_text: e.target.value,
                        }))
                      }
                    />

                    <textarea
                      rows={2}
                      className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                      placeholder="Default inspection note (optional)…"
                      value={newItem.inspection_note}
                      onChange={(e) =>
                        setNewItem((prev) => ({
                          ...prev,
                          inspection_note: e.target.value,
                        }))
                      }
                    />

                    <div className="flex flex-wrap gap-2">
                      <input
                        type="text"
                        className="flex-1 min-w-[80px] rounded border border-gray-200 bg-white px-2 py-1 text-[11px]"
                        placeholder="Category (optional)"
                        value={newItem.item_category}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            item_category: e.target.value,
                          }))
                        }
                      />
                      <input
                        type="number"
                        className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-[11px]"
                        placeholder="#"
                        value={newItem.order_index}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            order_index: e.target.value,
                          }))
                        }
                      />
                      <label className="inline-flex items-center gap-1 text-[11px] text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!newItem.is_required}
                          onChange={(e) =>
                            setNewItem((prev) => ({
                              ...prev,
                              is_required: e.target.checked,
                            }))
                          }
                        />
                        Required
                      </label>
                    </div>
                  </form>

                  {/* Items list */}
                  <div className="flex-1 overflow-auto border border-gray-100 rounded-md">
                    {checklistItems.length === 0 && !checklistLoading && (
                      <div className="p-3 text-[11px] text-gray-500">
                        No checklist items for this type yet.
                      </div>
                    )}

                    {checklistItems.length > 0 && (
                      <table className="min-w-full border-collapse text-[11px]">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="px-2 py-1 text-left font-medium text-gray-600 w-[45%]">
                              Text
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-600 w-[30%]">
                              Note
                            </th>
                            <th className="px-2 py-1 text-left font-medium text-gray-600">
                              Cat.
                            </th>
                            <th className="px-2 py-1 text-center font-medium text-gray-600">
                              Req.
                            </th>
                            <th className="px-2 py-1 text-center font-medium text-gray-600">
                              #
                            </th>
                            <th className="px-2 py-1 text-right font-medium text-gray-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {checklistItems.map((item) => {
                            const isSaving = itemSavingId === item.id;
                            const isDeleting = itemDeletingId === item.id;

                            return (
                              <tr
                                key={item.id}
                                className="border-b border-gray-50 hover:bg-gray-50"
                              >
                                {/* Text */}
                                <td className="px-2 py-1 align-top w-[45%]">
                                  <textarea
                                    rows={2}
                                    className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                                    value={item.item_text || ""}
                                    onChange={(e) =>
                                      updateItemLocal(item.id, {
                                        item_text: e.target.value,
                                      })
                                    }
                                  />
                                </td>

                                {/* Note */}
                                <td className="px-2 py-1 align-top w-[30%]">
                                  <textarea
                                    rows={2}
                                    className="w-full rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                                    placeholder="Default inspection note (optional)…"
                                    value={item.inspection_note || ""}
                                    onChange={(e) =>
                                      updateItemLocal(item.id, {
                                        inspection_note: e.target.value,
                                      })
                                    }
                                  />
                                </td>

                                {/* Category */}
                                <td className="px-2 py-1 align-top">
                                  <input
                                    type="text"
                                    className="w-20 rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                                    value={item.item_category || ""}
                                    onChange={(e) =>
                                      updateItemLocal(item.id, {
                                        item_category: e.target.value,
                                      })
                                    }
                                  />
                                </td>

                                {/* Required */}
                                <td className="px-2 py-1 align-top text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!item.is_required}
                                    onChange={(e) =>
                                      updateItemLocal(item.id, {
                                        is_required: e.target.checked,
                                      })
                                    }
                                  />
                                </td>

                                {/* Order index */}
                                <td className="px-2 py-1 align-top text-center">
                                  <input
                                    type="number"
                                    className="w-12 rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px]"
                                    value={
                                      item.order_index === null ||
                                      item.order_index === undefined
                                        ? ""
                                        : item.order_index
                                    }
                                    onChange={(e) =>
                                      updateItemLocal(item.id, {
                                        order_index: e.target.value,
                                      })
                                    }
                                  />
                                </td>

                                {/* Actions */}
                                <td className="px-2 py-1 align-top text-right">
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleSaveItem(item)}
                                      disabled={isSaving}
                                      className={[
                                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-white",
                                        isSaving
                                          ? "bg-gray-400 cursor-not-allowed"
                                          : "bg-gray-900 hover:bg-black",
                                      ].join(" ")}
                                    >
                                      <Save className="w-3 h-3" />
                                      {isSaving ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteItem(item.id)}
                                      disabled={isDeleting}
                                      className={[
                                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
                                        isDeleting
                                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                          : "bg-red-50 text-red-700 hover:bg-red-100",
                                      ].join(" ")}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      {isDeleting ? "Deleting…" : "Delete"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========= AI DESIGN TAB (NEW) ========= */}
      {activeSubtab === "requirement-intelligence" && (
        <RequirementIntelligenceView />
      )}

      {/* Legacy AI Design is intentionally retained but no longer exposed in the tab list. */}
      {activeSubtab === "ai-design" && (
        <div className="p-6 space-y-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Header */}
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold text-gray-900">
              AI-Designed Inspection Templates (RAG)
            </h2>
            <p className="text-sm text-gray-500">
              Use project specifications + SmokyQ-AI to design inspection types
              and checklist items. Saved templates will be available when
              creating inspections. Current templates in this project:{" "}
              {inspectionTypesLoading ? "…" : currentCount}
            </p>
          </div>

          {/* Controls: focus + instructions + Generate */}
          <div className="bg-gray-50 rounded-lg border p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">
                  Focus scope / spec context
                </label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                  placeholder="e.g. Switchboards, Fire Alarm, Section 260500"
                  value={focusScope}
                  onChange={(e) => setFocusScope(e.target.value)}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Optional. Narrow the RAG context (discipline, system, spec
                  section).
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-700">
                  Design instructions to SmokyQ-AI
                </label>
                <textarea
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm min-h-[68px] bg-white"
                  placeholder={
                    'e.g. "Generate 5 inspection types with 3 checklist items each, ' +
                    'focused on LV switchboards and main electrical room."'
                  }
                  value={designInstructions}
                  onChange={(e) => setDesignInstructions(e.target.value)}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  You have full control. Describe exactly how many types/items
                  you want and what they should cover.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500">
                Generated content is a draft. You can edit or toggle items off
                before saving.
              </div>

              <button
                type="button"
                onClick={handleGenerateFromRag}
                disabled={loadingPreview}
                className={[
                  "inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white",
                  loadingPreview
                    ? "bg-gray-400 cursor-wait"
                    : "bg-orange-500 hover:bg-orange-600",
                ].join(" ")}
              >
                {loadingPreview ? "Generating…" : "Generate from project specs"}
              </button>
            </div>

            {error && (
              <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {error}
              </div>
            )}
          </div>

          {/* Preview area: clear visual separation between types & checklist */}
          {!hasPreview && !loadingPreview && !error && (
            <div className="border border-dashed border-gray-300 rounded-lg p-6 text-sm text-gray-500 text-center bg-white">
              No AI design preview yet. Enter a focus scope and/or instructions
              above, then click{" "}
              <span className="font-semibold">Generate from project specs</span>
              .
            </div>
          )}

          {hasPreview && (
            <div className="flex flex-col gap-4 lg:flex-row">
              {/* Inspection Types */}
              <div className="flex-1 bg-white rounded-lg shadow border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Inspection Types
                    </div>
                    <div className="text-xs text-gray-500">
                      {draftTypes.length} proposed types
                    </div>
                  </div>
                </div>

                {draftTypes.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    No inspection types returned from the LLM.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="p-2 text-left w-10">Use</th>
                          <th className="p-2 text-left">Spec / Scope</th>
                          <th className="p-2 text-left">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draftTypes.map((t) => (
                          <tr key={t.id} className="border-t">
                            <td className="p-2 align-top">
                              <input
                                type="checkbox"
                                checked={!!t.enabled}
                                onChange={() => toggleTypeEnabled(t.id)}
                              />
                            </td>
                            <td className="p-2 align-top">
                              <input
                                type="text"
                                className="w-full border rounded-md px-2 py-1 text-xs"
                                value={t.spec || ""}
                                onChange={(e) =>
                                  updateTypeField(t.id, "spec", e.target.value)
                                }
                                placeholder="e.g. Switchboard, Section 260500"
                              />
                            </td>
                            <td className="p-2 align-top">
                              <textarea
                                className="w-full border rounded-md px-2 py-1 text-xs min-h-[52px]"
                                value={t.description || ""}
                                onChange={(e) =>
                                  updateTypeField(
                                    t.id,
                                    "description",
                                    e.target.value,
                                  )
                                }
                                placeholder="Short description of what this inspection covers."
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Checklist Items */}
              <div className="flex-1 bg-white rounded-lg shadow border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Checklist Items
                    </div>
                    <div className="text-xs text-gray-500">
                      {draftChecklists.length} proposed items
                    </div>
                  </div>
                </div>

                {draftChecklists.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    No checklist items returned from the LLM.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="p-2 text-left w-10">Use</th>
                          <th className="p-2 text-left w-1/2">
                            Checklist Item
                          </th>
                          <th className="p-2 text-left w-1/2">
                            Inspection Note / Guidance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {draftChecklists.map((it) => (
                          <tr key={it.id} className="border-t">
                            <td className="p-2 align-top">
                              <input
                                type="checkbox"
                                checked={!!it.enabled}
                                onChange={() => toggleChecklistEnabled(it.id)}
                              />
                            </td>
                            <td className="p-2 align-top">
                              <textarea
                                className="w-full border rounded-md px-2 py-1 text-xs min-h-[52px]"
                                value={it.item_text || ""}
                                onChange={(e) =>
                                  updateChecklistField(
                                    it.id,
                                    "item_text",
                                    e.target.value,
                                  )
                                }
                                placeholder="Short, actionable verification text."
                              />
                            </td>
                            <td className="p-2 align-top">
                              <textarea
                                className="w-full border rounded-md px-2 py-1 text-xs min-h-[52px]"
                                value={it.inspection_note || ""}
                                onChange={(e) =>
                                  updateChecklistField(
                                    it.id,
                                    "inspection_note",
                                    e.target.value,
                                  )
                                }
                                placeholder="Optional inspector note / how to verify."
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Save bar */}
          {hasPreview && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                Only rows with <span className="font-semibold">Use</span>{" "}
                checked will be saved as project inspection templates.
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPreview(null);
                    setDraftTypes([]);
                    setDraftChecklists([]);
                    setError(null);
                  }}
                  disabled={saving}
                  className="px-4 py-2 rounded-md border text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Discard preview
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className={[
                    "inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white",
                    saving
                      ? "bg-gray-400 cursor-wait"
                      : "bg-gray-900 hover:bg-black",
                  ].join(" ")}
                >
                  {saving ? "Saving…" : "Save templates"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionTemplatesView;
